import rasService from '@/services/api/RAS/ras.service'
import {
  // Getters
  SUMMARY_LOADING,
  SUMMARY_ERROR,
  MISSED_OPP_LOADING,
  MISSED_OPP_ERROR,
  ACCOUNTS_LOADING,
  ACCOUNTS_ERROR,
  SUMMARY_RAS_BENEFIT,
  SUMMARY_DIVIDENDS_PAID,
  SUMMARY_BENEFITS_CLAIMED_PCT,
  SUMMARY_TRANSACTIONS_PROCESSED,
  SUMMARY_TOTAL_MISSED_BENEFIT,
  SUMMARY_PAYMENTS_BY_YEAR,
  SUMMARY_PAYMENTS_BY_MONTH,
  SUMMARY_BENEFIT_BY_COUNTRY,
  SUMMARY_HIGHEST_VALUE_ACCOUNTS,
  MISSED_OPP_MISSED_BENEFITS,
  MISSED_OPP_PARTIAL_SERVICE_ACCOUNTS,
  MISSED_OPP_BENEFITS_MISSED_PCT,
  MISSED_OPP_INCOMPLETE_TRANSACTIONS,
  MISSED_OPP_RAS_BENEFIT,
  MISSED_OPP_BY_YEAR,
  MISSED_OPP_BY_MONTH,
  MISSED_OPP_BY_COUNTRY,
  MISSED_OPP_HIGHEST_VALUE_ACCOUNTS,
  ACCOUNTS_LIST,
  TRANSACTIONS_EXPORT_LOADING,
  TRANSACTIONS_EXPORT_ERROR,

  // Mutations
  SET_SUMMARY_LOADING,
  SET_SUMMARY_ERROR,
  SET_SUMMARY_DATA,
  SET_MISSED_OPP_LOADING,
  SET_MISSED_OPP_ERROR,
  SET_MISSED_OPP_DATA,
  SET_ACCOUNTS_LOADING,
  SET_ACCOUNTS_ERROR,
  SET_ACCOUNTS_DATA,
  SET_TRANSACTIONS_EXPORT_LOADING,
  SET_TRANSACTIONS_EXPORT_ERROR,

  // Actions
  FETCH_SUMMARY,
  FETCH_MISSED_OPPORTUNITIES,
  FETCH_ACCOUNTS,
  EXPORT_TRANSACTIONS,
} from '../relief-at-source-types'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})
const commaFormatter = new Intl.NumberFormat('en-US')
const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
})

const formatCurrency = (n) => (n == null ? null : currencyFormatter.format(n))
const formatComma = (n) => (n == null ? null : commaFormatter.format(n))
const formatPercent = (n) => (n == null ? null : percentFormatter.format(n))

// Sums each RasTimeSeries (one per year) into a single yearly total, producing
// the chronologically sorted { arg, val } array RdBarYear expects. Uses plain
// year strings (not Dates) so DevExtreme doesn't auto-pick month-level
// gridlines/labels for a short date range (see Reclaim Dashboard's
// PAID_BY_YEAR_GROUPS getter, which uses the same string-year approach).
function aggregateTimeSeriesByYear(series) {
  if (!series) return []
  return series
    .map((s) => ({
      arg: s.seriesId.toString(),
      val: s.points.reduce((sum, p) => sum + p.value, 0),
    }))
    .sort((a, b) => a.arg.localeCompare(b.arg))
}

// Flattens the API's array-of-series shape into the single chronologically
// sorted { arg, val } array RdBarYear expects, at month-level granularity.
// Uses the raw "YYYY-MM" period string (not a Date) so the range selector
// treats it as a discrete scale, matching year view's string-based approach
// and avoiding continuous-scale tick/snapping artifacts.
function flattenTimeSeriesByMonth(series) {
  if (!series) return []
  return series
    .flatMap((s) => s.points)
    .map((p) => ({ arg: p.period, val: p.value }))
    .sort((a, b) => a.arg.localeCompare(b.arg))
}

// Maps the API's country share shape into the { arg, val, netReclaim } shape
// RdPie expects (its label formatter reads point.data.netReclaim).
function mapCountryShares(countries) {
  if (!countries) return []
  return countries.map((c) => ({
    arg: c.countryName,
    val: c.value,
    netReclaim: c.value,
  }))
}

// Maps a RasAccount into the row shape RdHighestValueAccountsGrid/
// RdAccountsListGrid expect. `rowKey` (not `id`, which the API doesn't
// guarantee is unique/non-empty) is what the grids use as their key-expr.
function mapAccount(account, index) {
  return {
    rowKey: index,
    id: account.accountId,
    dtcParticipant: account.dtcParticipant,
    acct: account.accountNumber,
    bo: account.beneficialOwner,
    entityType: account.entityType,
    cor: account.countryOfResidence,
    procCode: account.procCode,
    serviceLevel: account.serviceLevel,
    rasBenefit: account.rasBenefit,
    missedBenefits: account.missedBenefits,
    numElections: account.numberOfElections,
    dividendsPaid: account.dividendsPaid,
  }
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

const state = {
  summaryLoading: false,
  summaryError: null,
  missedOppLoading: false,
  missedOppError: null,
  accountsLoading: false,
  accountsError: null,

  benefitSummary: null,
  missedOpportunities: null,
  accounts: [],

  transactionsExportLoading: false,
  transactionsExportError: null,
}

const getters = {
  [SUMMARY_LOADING](state) {
    return state.summaryLoading
  },
  [SUMMARY_ERROR](state) {
    return state.summaryError
  },
  [MISSED_OPP_LOADING](state) {
    return state.missedOppLoading
  },
  [MISSED_OPP_ERROR](state) {
    return state.missedOppError
  },
  [ACCOUNTS_LOADING](state) {
    return state.accountsLoading
  },
  [ACCOUNTS_ERROR](state) {
    return state.accountsError
  },
  [SUMMARY_RAS_BENEFIT](state) {
    return formatCurrency(state.benefitSummary?.rasBenefit)
  },
  [SUMMARY_DIVIDENDS_PAID](state) {
    return formatComma(state.benefitSummary?.dividendsPaid)
  },
  [SUMMARY_BENEFITS_CLAIMED_PCT](state) {
    return formatPercent(state.benefitSummary?.benefitsClaimedPct)
  },
  [SUMMARY_TRANSACTIONS_PROCESSED](state) {
    return formatComma(state.benefitSummary?.transactionsProcessed)
  },
  [SUMMARY_TOTAL_MISSED_BENEFIT](state) {
    return formatCurrency(state.benefitSummary?.totalMissedBenefit)
  },
  [SUMMARY_PAYMENTS_BY_YEAR](state) {
    return aggregateTimeSeriesByYear(
      state.benefitSummary?.paymentsByMonthAndYear,
    )
  },
  [SUMMARY_PAYMENTS_BY_MONTH](state) {
    return flattenTimeSeriesByMonth(
      state.benefitSummary?.paymentsByMonthAndYear,
    )
  },
  [SUMMARY_BENEFIT_BY_COUNTRY](state) {
    return mapCountryShares(state.benefitSummary?.benefitByCountry)
  },
  [SUMMARY_HIGHEST_VALUE_ACCOUNTS](state) {
    return (state.benefitSummary?.highestValueAccounts ?? []).map(mapAccount)
  },

  [MISSED_OPP_MISSED_BENEFITS](state) {
    return formatCurrency(state.missedOpportunities?.missedBenefits)
  },
  [MISSED_OPP_PARTIAL_SERVICE_ACCOUNTS](state) {
    return formatComma(state.missedOpportunities?.partialServiceAccounts)
  },
  [MISSED_OPP_BENEFITS_MISSED_PCT](state) {
    return formatPercent(state.missedOpportunities?.benefitsMissedPct)
  },
  [MISSED_OPP_INCOMPLETE_TRANSACTIONS](state) {
    return formatComma(state.missedOpportunities?.incompleteTransactions)
  },
  [MISSED_OPP_RAS_BENEFIT](state) {
    return formatCurrency(state.missedOpportunities?.rasBenefit)
  },
  [MISSED_OPP_BY_YEAR](state) {
    return aggregateTimeSeriesByYear(
      state.missedOpportunities?.missedBenefitsOverTime,
    )
  },
  [MISSED_OPP_BY_MONTH](state) {
    return flattenTimeSeriesByMonth(
      state.missedOpportunities?.missedBenefitsOverTime,
    )
  },
  [MISSED_OPP_BY_COUNTRY](state) {
    return mapCountryShares(state.missedOpportunities?.missedBenefitsByCountry)
  },
  [MISSED_OPP_HIGHEST_VALUE_ACCOUNTS](state) {
    return (state.missedOpportunities?.highestValueBenefitsMissed ?? []).map(
      mapAccount,
    )
  },

  [ACCOUNTS_LIST](state) {
    return state.accounts.map(mapAccount)
  },
  [TRANSACTIONS_EXPORT_LOADING](state) {
    return state.transactionsExportLoading
  },
  [TRANSACTIONS_EXPORT_ERROR](state) {
    return state.transactionsExportError
  },
}

const mutations = {
  [SET_SUMMARY_LOADING](state, payload) {
    state.summaryLoading = payload
  },
  [SET_SUMMARY_ERROR](state, payload) {
    state.summaryError = payload
  },
  [SET_SUMMARY_DATA](state, payload) {
    state.benefitSummary = payload
  },
  [SET_MISSED_OPP_LOADING](state, payload) {
    state.missedOppLoading = payload
  },
  [SET_MISSED_OPP_ERROR](state, payload) {
    state.missedOppError = payload
  },
  [SET_MISSED_OPP_DATA](state, payload) {
    state.missedOpportunities = payload
  },
  [SET_ACCOUNTS_LOADING](state, payload) {
    state.accountsLoading = payload
  },
  [SET_ACCOUNTS_ERROR](state, payload) {
    state.accountsError = payload
  },
  [SET_ACCOUNTS_DATA](state, payload) {
    state.accounts = payload
  },
  [SET_TRANSACTIONS_EXPORT_LOADING](state, payload) {
    state.transactionsExportLoading = payload
  },
  [SET_TRANSACTIONS_EXPORT_ERROR](state, payload) {
    state.transactionsExportError = payload
  },
}

const actions = {
  async [FETCH_SUMMARY]({ commit, rootState }) {
    commit(SET_SUMMARY_LOADING, true)
    commit(SET_SUMMARY_ERROR, null)
    try {
      const data = await rasService.getDashboard({
        tab: 'benefit_summary',
        viewAsUserId: rootState.reports.viewReportAs?.userId ?? 0,
      })
      if (!data || !data.benefitSummary) {
        throw new Error('Invalid benefit summary dashboard response.')
      }
      commit(SET_SUMMARY_DATA, data.benefitSummary)
    } catch (error) {
      commit(SET_SUMMARY_ERROR, error)
    } finally {
      commit(SET_SUMMARY_LOADING, false)
    }
  },

  async [FETCH_MISSED_OPPORTUNITIES]({ commit, rootState }) {
    commit(SET_MISSED_OPP_LOADING, true)
    commit(SET_MISSED_OPP_ERROR, null)
    try {
      const data = await rasService.getDashboard({
        tab: 'missed_opportunities',
        viewAsUserId: rootState.reports.viewReportAs?.userId ?? 0,
      })
      if (!data || !data.missedOpportunities) {
        throw new Error('Invalid missed opportunities dashboard response.')
      }
      commit(SET_MISSED_OPP_DATA, data.missedOpportunities)
    } catch (error) {
      commit(SET_MISSED_OPP_ERROR, error)
    } finally {
      commit(SET_MISSED_OPP_LOADING, false)
    }
  },

  async [FETCH_ACCOUNTS]({ commit, rootState }, sortBy) {
    commit(SET_ACCOUNTS_LOADING, true)
    commit(SET_ACCOUNTS_ERROR, null)
    try {
      const data = await rasService.getDashboard({
        tab: 'accounts',
        sortBy,
        viewAsUserId: rootState.reports.viewReportAs?.userId ?? 0,
      })
      if (!data || !Array.isArray(data.accounts)) {
        throw new Error('Invalid accounts dashboard response.')
      }
      commit(SET_ACCOUNTS_DATA, data.accounts)
    } catch (error) {
      commit(SET_ACCOUNTS_ERROR, error)
    } finally {
      commit(SET_ACCOUNTS_LOADING, false)
    }
  },

  async [EXPORT_TRANSACTIONS](
    { commit, rootState },
    { accountList = [] } = {},
  ) {
    commit(SET_TRANSACTIONS_EXPORT_LOADING, true)
    commit(SET_TRANSACTIONS_EXPORT_ERROR, null)
    try {
      const { blob, fileName } = await rasService.exportTransactions({
        viewAsUserId: rootState.reports.viewReportAs?.userId ?? 0,
        accountList,
      })
      if (!blob || blob.size === 0) {
        throw new Error('The transaction export returned an empty file.')
      }
      downloadBlob(blob, fileName)
    } catch (error) {
      commit(SET_TRANSACTIONS_EXPORT_ERROR, error)
    } finally {
      commit(SET_TRANSACTIONS_EXPORT_LOADING, false)
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
